import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import "../../../../../css/CashflowModeller2/Inputs/Inputs.css";
import "../../../../../css/CashflowModeller2/Inputs/InputDate.css";
import CalenderIcon from "../Buttons/CalenderIcon";

/**
 * InputDate is a controlled input component for date entry, supporting multiple date formats.
 * It provides both a formatted text input and a hidden native date/month/number picker for accessibility and device compatibility.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} [props.isDisabled] - If true, disables the input.
 * @param {string} [props.label] - Optional label to display above the input.
 * @param {string} [props.value] - The current value of the input, in the specified date format.
 * @param {function} [props.onChange] - Callback fired when the value changes. Receives the formatted date string.
 * @param {boolean} [props.isError] - If true, applies error styling to the input.
 * @param {boolean} [props.readOnly] - If true, makes the input read-only.
 * @param {string} [props.dateFormat="DD/MM/YYYY"] - The date format to use. Supported: "DD/MM/YYYY", "MM/YYYY", "YYYY".
 *
 * @returns {JSX.Element} The rendered date input component.
 */
const InputDate = ({
  isDisabled,
  label,
  value,
  onChange,
  isError,
  readOnly,
  dateFormat = "DD/MM/YYYY",
}) => {
  const [status, setStatus] = useState("default");
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const hiddenDateRef = useRef(null);

  // --- Sync external value to local ---
  useEffect(() => {
    if (value) {
      const parsed = dayjs(value, [dateFormat, "YYYY-MM-DD"]);
      if (parsed.isValid()) {
        setInputValue(parsed.format(dateFormat));
      } else {
        setInputValue(value);
      }
    } else {
      setInputValue("");
    }
  }, [value, dateFormat]);


  /**
   * Determines the maximum length of the date input based on the specified date format.
   *
   * - If the format is "YYYY", the maximum length is 4.
   * - If the format is "MM/YYYY", the maximum length is 6 (2 for month + 4 for year).
   * - For other formats (e.g., "DDMMYYYY"), the maximum length is 8.
   *
   * @type {number}
   */
  const maxLength = (() => {
    if (dateFormat === "YYYY") return 4;
    if (dateFormat === "MM/YYYY") return 6; // 2 + 4
    return 8; // DDMMYYYY
  })();

  
  /**
   * Formats a raw date string input based on the specified date format.
   *
   * Supported formats:
   * - "YYYY": Returns a 4-digit year. If only 2 digits are provided, auto-completes to 20XX.
   * - "MM/YYYY": Returns a month and year in MM/YYYY format. If less than 3 digits, returns only the month.
   * - "DD/MM/YYYY": Returns a date in DD/MM/YYYY format. Handles partial input for day and month.
   *
   * @param {string} raw - The raw input string representing a date.
   * @returns {string} The formatted date string according to the selected date format.
   */
  const formatValue = (raw) => {
    if (dateFormat === "YYYY") {
      if (raw.length === 2) return `20${raw}`; // auto-complete 25 → 2025
      return raw.slice(0, 4);
    }
    if (dateFormat === "MM/YYYY") {
      if (raw.length >= 2) return `${raw.slice(0, 2)}/${raw.slice(2, 6)}`;
      return raw.slice(0, 2);
    }
    // DD/MM/YYYY
    if (raw.length >= 4)
      return `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4, 8)}`;
    if (raw.length >= 2) return `${raw.slice(0, 2)}/${raw.slice(2, 4)}`;
    return raw.slice(0, 2);
  };

  
  /**
   * Handles changes to the date input field.
   *
   * - Extracts numeric digits from the input, up to a maximum length.
   * - Formats the extracted digits into a date string.
   * - Updates the input value state with the formatted date.
   * - If the formatted date is valid according to the specified date format,
   *   calls the `onChange` callback with the parsed date string.
   * - If invalid, calls `onChange` with the raw formatted value.
   * - Ensures the caret remains at the end of the input after formatting.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleInputChange = (e) => {
    const rawDigits = e.target.value.replace(/[^0-9]/g, "").slice(0, maxLength);
    const formatted = formatValue(rawDigits);

    const input = inputRef.current;
    const pos = input.selectionStart;

    setInputValue(formatted);

    const parsed = dayjs(formatted, dateFormat, true);
    if (parsed.isValid()) {
      onChange?.(parsed.format(dateFormat));
    } else {
      onChange?.(formatted);
    }

    setTimeout(() => {
      if (input) {
        input.setSelectionRange(formatted.length, formatted.length);
      }
    }, 0);
  };

  
  /**
   * Handles the change event for a native date input.
   * Parses the input value, validates it using dayjs, formats it according to the specified date format,
   * and updates the input value and status accordingly.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the date input.
   * @returns {void}
   */
  const handleNativeChange = (e) => {
    const raw = e.target.value;
    let output = "";

    if (dayjs(raw).isValid()) {
      output = dayjs(raw).format(dateFormat);
    }

    onChange?.(output);
    setInputValue(output);
    setStatus("selected");
  };

  /**
   * Determines the appropriate input type for a date input field based on the specified date format.
   *
   * @returns {string} The input type to be used:
   *   - "month" if the date format is "MM/YYYY"
   *   - "number" if the date format is "YYYY"
   *   - "date" for all other formats
   */
  const getInputType = () => {
    if (dateFormat === "MM/YYYY") return "month";
    if (dateFormat === "YYYY") return "number";
    return "date";
  };

  /**
   * Converts the input date value to a native date string format ("YYYY-MM-DD") 
   * based on the specified date format.
   *
   * @returns {string} The formatted date string in "YYYY-MM-DD" format, or an empty string if no value is provided.
   *
   * @example
   * // Assuming dateFormat is "MM/YYYY" and value is "06/2024"
   * getNativeValue(); // returns "2024-06"
   *
   * @example
   * // Assuming dateFormat is "YYYY" and value is "2024"
   * getNativeValue(); // returns "2024-01"
   *
   * @example
   * // Assuming dateFormat is "DD/MM/YYYY" and value is "15/06/2024"
   * getNativeValue(); // returns "2024-06-15"
   */
  const getNativeValue = () => {
    if (!value) return "";
    if (dateFormat === "MM/YYYY") {
      const [month, year] = value.split("/");
      return `${year}-${month}`;
    }
    if (dateFormat === "YYYY") {
      return `${value}-01`;
    }
    return dayjs(value, "DD/MM/YYYY").format("YYYY-MM-DD");
  };

  return (
    <div className={`${label ? "input-label-container" : ""}`}>
      {label && <div className="Label">{label}</div>}

      <div
        className={`input-container ${isDisabled ? "disabled" : ""} ${status} ${
          isError ? "error" : ""
        } ${readOnly ? "readOnly" : ""}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={
            dateFormat === "MM/YYYY"
              ? "09/2025"
              : dateFormat === "YYYY"
              ? "2025"
              : "11/09/2025"
          }
          className="input-date input-field Input"
          onFocus={() => !readOnly && setStatus("selected")}
          onBlur={() => setStatus("default")}
          onChange={handleInputChange}
          disabled={isDisabled}
          readOnly={readOnly}
        />

        <input
          ref={hiddenDateRef}
          type={getInputType()}
          value={getNativeValue()}
          onChange={handleNativeChange}
          readOnly={readOnly}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        />

        {!readOnly && (
          <div
            className="input-icon-container calendar-icon"
            onClick={() => {
              if (!readOnly && !isDisabled) {
                hiddenDateRef.current?.showPicker?.();
              }
            }}
          >
            <CalenderIcon isDisabled={isDisabled} />
          </div>
        )}
      </div>
    </div>
  );
};

export default InputDate;
